import React, { useState, useEffect } from 'react';
import logo from './TBL Logo White.png';
import ClassVersionSelection from './components/ClassVersionSelection';
import MainNavigation from './components/MainNavigation';
import TryEachWine from './components/TryEachWine';
import RateEachWine from './components/RateEachWine';
import BlendingTrials from './components/BlendingTrials';
import FinalBlend from './components/FinalBlend';
import WineVarietals from './components/WineVarietals';
import UnderstandPreferences from './components/UnderstandPreferences';
import ClassSummary from './components/ClassSummary';
import './App.css';

function App() {
  const [currentScreen, setCurrentScreen] = useState('home'); // 'home', 'version', 'navigation', 'module', 'summary'
  const [classVersion, setClassVersion] = useState(null);
  const [currentModule, setCurrentModule] = useState(null);

  // Track session start time for analytics
  useEffect(() => {
    if (!sessionStorage.getItem('sessionStartTime')) {
      sessionStorage.setItem('sessionStartTime', Date.now().toString());
    }
  }, []);

  // Handle browser back button to prevent data loss
  useEffect(() => {
    const handlePopState = (event) => {
      // Check if user has any saved data
      const hasData = sessionStorage.getItem('wineRatings') || 
                     sessionStorage.getItem('quizAnswers') || 
                     sessionStorage.getItem('wineCharacteristicRatings') || 
                     sessionStorage.getItem('finalBlendData') || 
                     sessionStorage.getItem('wineVarietals');
      
      if (hasData && currentScreen !== 'home') {
        // User has data and is not on home screen - redirect to home instead of leaving
        event.preventDefault();
        setCurrentScreen('home');
        setClassVersion(null);
        setCurrentModule(null);
        window.scrollTo(0, 0);
        
        // Push a new state to prevent the back button from working again immediately
        window.history.pushState(null, '', window.location.href);
      }
    };

    // Add event listener for browser back button
    window.addEventListener('popstate', handlePopState);
    
    // Push initial state to enable back button detection
    window.history.pushState(null, '', window.location.href);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentScreen]);

  // Handle page unload to warn users about data loss
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Check if user has any saved data
      const hasData = sessionStorage.getItem('wineRatings') || 
                     sessionStorage.getItem('quizAnswers') || 
                     sessionStorage.getItem('wineCharacteristicRatings') || 
                     sessionStorage.getItem('finalBlendData') || 
                     sessionStorage.getItem('wineVarietals');
      
      if (hasData && currentScreen !== 'home') {
        // Show warning message
        const message = 'You have unsaved progress in your wine blending class. Are you sure you want to leave?';
        event.returnValue = message;
        return message;
      }
    };

    // Add event listener for page unload
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentScreen]);

  const handleStartClass = () => {
    setCurrentScreen('version');
    window.scrollTo(0, 0);
  };

  const handleVersionSelect = (version) => {
    setClassVersion(version);
    setCurrentScreen('navigation');
    window.scrollTo(0, 0);
  };

  const handleModuleSelect = (moduleId) => {
    setCurrentModule(moduleId);
    setCurrentScreen('module');
    window.scrollTo(0, 0);
  };

  const handleBackToHome = () => {
    setCurrentScreen('home');
    setClassVersion(null);
    setCurrentModule(null);
    window.scrollTo(0, 0);
  };

  const handleBackToVersion = () => {
    setCurrentScreen('version');
    setClassVersion(null);
    setCurrentModule(null);
    window.scrollTo(0, 0);
  };

  const handleBackToNavigation = () => {
    setCurrentScreen('navigation');
    setCurrentModule(null);
    window.scrollTo(0, 0);
  };

  const handleCompleteClass = () => {
    setCurrentScreen('summary');
    setCurrentModule(null);
    window.scrollTo(0, 0);
  };

  // Smart navigation to next module in sequence
  const handleNextModule = () => {
    const moduleSequence = [
      'try-each-wine',
      'understand-preferences', 
      'rate-each-wine',
      'blending-trials',
      'final-blend',
      'wine-varietals'
    ];
    
    const currentIndex = moduleSequence.indexOf(currentModule);
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < moduleSequence.length) {
      // Go to next module
      setCurrentModule(moduleSequence[nextIndex]);
      setCurrentScreen('module');
    } else {
      // Last module completed, go to summary
      setCurrentScreen('summary');
      setCurrentModule(null);
    }
    window.scrollTo(0, 0);
  };

  // Render different screens based on current state
  if (currentScreen === 'version') {
    return <ClassVersionSelection onVersionSelect={handleVersionSelect} onBack={handleBackToHome} />;
  }

  if (currentScreen === 'navigation') {
    return <MainNavigation classVersion={classVersion} onModuleSelect={handleModuleSelect} onBack={handleBackToVersion} />;
  }

  if (currentScreen === 'summary') {
    return <ClassSummary classVersion={classVersion} onBack={handleBackToHome} onBackToNavigation={handleBackToNavigation} />;
  }

  if (currentScreen === 'module') {
    // Render specific module components
    if (currentModule === 'try-each-wine') {
      return <TryEachWine classVersion={classVersion} onNext={handleNextModule} onBack={handleBackToNavigation} />;
    }
    
    if (currentModule === 'rate-each-wine') {
      return <RateEachWine classVersion={classVersion} onNext={handleNextModule} onBack={handleBackToNavigation} />;
    }
    
    if (currentModule === 'blending-trials') {
      return <BlendingTrials classVersion={classVersion} onNext={handleNextModule} onBack={handleBackToNavigation} />;
    }
    
    if (currentModule === 'final-blend') {
      return <FinalBlend classVersion={classVersion} onNext={handleNextModule} onBack={handleBackToNavigation} />;
    }
    
    if (currentModule === 'wine-varietals') {
      return <WineVarietals classVersion={classVersion} onNext={handleCompleteClass} onBack={handleBackToNavigation} />;
    }
    
    if (currentModule === 'understand-preferences') {
      return <UnderstandPreferences classVersion={classVersion} onNext={handleNextModule} onBack={handleBackToNavigation} />;
    }
    
    // Placeholder for other modules
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'white', minHeight: '100vh', background: classVersion === 'red' ? '#8B0000' : '#DAA520' }}>
        <h1>Module: {currentModule}</h1>
        <p>This module is coming soon!</p>
        <button onClick={handleBackToNavigation} style={{ padding: '10px 20px', marginTop: '20px' }}>
          Back to Navigation
        </button>
      </div>
    );
  }

  // Check if user has saved progress
  const hasSavedProgress = () => {
    return sessionStorage.getItem('wineRatings') || 
           sessionStorage.getItem('quizAnswers') || 
           sessionStorage.getItem('wineCharacteristicRatings') || 
           sessionStorage.getItem('finalBlendData') || 
           sessionStorage.getItem('wineVarietals');
  };

  return (
    <div className="App">
      <header className="App-header">
        <img src={logo} className="App-logo" alt="logo" />
        <h1>WINE BLENDING CLASS</h1>
        <p>
          THE FUTURE OF WINE IS HERE. DISCOVER YOUR PERFECT WINE MATCH.
        </p>
        {hasSavedProgress() && (
          <div style={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.1)', 
            padding: '15px', 
            borderRadius: '8px', 
            margin: '20px 0',
            border: '2px solid rgba(255, 255, 255, 0.3)'
          }}>
            <p style={{ margin: '0 0 10px 0', fontSize: '1.1rem', fontWeight: 'bold' }}>
              📚 You have saved progress!
            </p>
            <p style={{ margin: '0', fontSize: '0.9rem', opacity: 0.9 }}>
              Your wine blending class progress has been saved. Click "START THE CLASS" to continue where you left off.
            </p>
          </div>
        )}
        <button 
          className="start-quiz-button"
          onClick={handleStartClass}
        >
          {hasSavedProgress() ? 'CONTINUE CLASS' : 'START THE CLASS'}
        </button>
        <a
          className="App-link"
          href="https://thewineblendinglab.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          thewineblendinglab.com
        </a>
      </header>
    </div>
  );
}

export default App;
